import React from "react";
import { Pressable, View } from "react-native";
import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTextChip from "@toothfairy/shared-ui/AppTextChip";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import { BUILTIN_TOOL_DESCRIPTIONS } from "./ToolOptionLabel";

const getDesc = (item) =>
  item?.description || BUILTIN_TOOL_DESCRIPTIONS[item?.name] || "";

const InsightRow = ({
  item,
  kind,
  currentTheme,
  t,
  getTypeLabel,
  history,
  workspaceId,
}) => {
  const desc = getDesc(item);
  const typeLabel =
    kind === "hook"
      ? t?.("settings.qa.hook", { defaultValue: "Hook" })
      : getTypeLabel
      ? getTypeLabel(item)
      : item?.type;
  const chipColor =
    item?.backgroundColor || currentTheme?.theme?.primary || "#1db8c6";

  const handlePress = () => {
    if (!history || !workspaceId || !item?.id) return;
    const section = kind === "hook" ? "exec_envs" : "functions";
    history.push(`/workspaces/${workspaceId}/settings/${section}/${item.id}`);
  };

  const baseStyle = {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: currentTheme?.theme?.border_color || "#ececec",
  };

  const renderContent = (hovered) => (
    <>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <AppText
          style={{
            flex: 1,
            fontWeight: "600",
            color: hovered
              ? currentTheme?.theme?.primary
              : currentTheme?.theme?.font_color,
            fontSize: currentTheme?.theme?.small_font_size,
          }}
        >
          {item?.name || item?.label}
        </AppText>
        {typeLabel ? (
          <AppTextChip
            label={`${typeLabel}`}
            borderColor={chipColor}
            backgroundColor={currentTheme?.theme?.transparent || "transparent"}
            fontColor={currentTheme?.theme?.font_color}
            iconSize={10}
            styleContainer={{
              marginRight: 0,
              marginLeft: 0,
              paddingVertical: 2,
              paddingHorizontal: 8,
              flexShrink: 0,
            }}
            styleLabel={{
              fontSize: currentTheme?.theme?.extra_small_font_size || 11,
            }}
          />
        ) : null}
      </View>
      {desc ? (
        <AppText
          style={{
            marginTop: 4,
            color: currentTheme?.theme?.mediumGrey,
            fontSize: currentTheme?.theme?.extra_small_font_size || 12,
            lineHeight: 17,
          }}
        >
          {desc}
        </AppText>
      ) : null}
    </>
  );

  if (!history || !workspaceId || !item?.id)
    return <View style={baseStyle}>{renderContent(false)}</View>;

  return (
    <Pressable style={baseStyle} onPress={handlePress}>
      {({ hovered }) => (
        <View style={{ cursor: "pointer" }}>{renderContent(hovered)}</View>
      )}
    </Pressable>
  );
};

// Right-side panel that explains the tools/hooks feature and live-lists the
// selected ones (with a type chip + what each does). Shows AppPlaceholderView
// when nothing is selected yet. Each item is clickable to open its settings page.
const ToolsInsightPanel = ({
  tools = [],
  hooks = [],
  currentTheme,
  t,
  getTypeLabel,
  history,
  workspaceId,
}) => {
  const hasTools = Array.isArray(tools) && tools.length > 0;
  const hasHooks = Array.isArray(hooks) && hooks.length > 0;
  const isEmpty = !hasTools && !hasHooks;

  if (isEmpty) {
    return (
      <View
        style={{
          flex: 1,
          // maxWidth: 600,
          minWidth: 260,
          borderWidth: 1,
          borderColor: currentTheme?.theme?.border_color || "#ececec",
          borderRadius: 12,
          overflow: "hidden",
          backgroundColor:
            currentTheme?.theme?.backgroundContrastColor ||
            currentTheme?.theme?.backgroundColor,
        }}
      >
        <AppPlaceholderView
          icon={AppAssets.icons.advanced}
          size={30}
          wrapperStyle={{ marginHorizontal: 0, height: "100%", padding: 20 }}
          mainCaption={t?.("settings.qa.toolsHooksEmptyTitle", {
            defaultValue: "Tools & execution hooks",
          })}
          subCaption={t?.("settings.qa.toolsHooksEmptySubtitle", {
            defaultValue:
              "Extend your agent with tools (APIs, databases, MCP servers, custom code) and execution hooks (predefined code environments). Select them on the left to see what each one does here.",
          })}
        />
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        maxWidth: 600,
        minWidth: 260,
        borderWidth: 1,
        borderColor: currentTheme?.theme?.border_color || "#ececec",
        borderRadius: 12,
        padding: 16,
        backgroundColor:
          currentTheme?.theme?.backgroundContrastColor ||
          currentTheme?.theme?.backgroundColor,
      }}
    >
      <AppText
        style={{
          fontWeight: "700",
          marginBottom: 12,
          color: currentTheme?.theme?.font_color,
          fontSize: currentTheme?.theme?.small_font_size,
        }}
      >
        {t?.("settings.qa.whatYouEnabled", {
          defaultValue: "What this agent can do",
        })}
      </AppText>

      {hasTools && (
        <>
          <AppText
            style={{
              marginBottom: 6,
              color: currentTheme?.theme?.mediumGrey,
              fontSize: currentTheme?.theme?.extra_small_font_size || 11,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {t?.("tools.title", { defaultValue: "Tools" })}
          </AppText>
          {tools.map((item, i) => (
            <InsightRow
              key={`tool-${item?.id || i}`}
              item={item}
              kind="tool"
              currentTheme={currentTheme}
              t={t}
              getTypeLabel={getTypeLabel}
              history={history}
              workspaceId={workspaceId}
            />
          ))}
        </>
      )}

      {hasHooks && (
        <>
          <AppText
            style={{
              marginTop: hasTools ? 6 : 0,
              marginBottom: 6,
              color: currentTheme?.theme?.mediumGrey,
              fontSize: currentTheme?.theme?.extra_small_font_size || 11,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {t?.("settings.qa.executionHooks", {
              defaultValue: "Execution hooks",
            })}
          </AppText>
          {hooks.map((item, i) => (
            <InsightRow
              key={`hook-${item?.id || i}`}
              item={item}
              kind="hook"
              currentTheme={currentTheme}
              t={t}
              getTypeLabel={getTypeLabel}
              history={history}
              workspaceId={workspaceId}
            />
          ))}
        </>
      )}
    </View>
  );
};

export default ToolsInsightPanel;
